import React, { useEffect, useState } from "react";
import { getInstantlyCampaigns, pushToInstantly, getPushSummary } from "../../lib/api";
import { useToast } from "../../hooks/use-toast";
import { Send, X, Loader2, CheckCircle2, Info, ExternalLink, RotateCcw, TrendingUp } from "lucide-react";

// Reusable Push-to-Instantly modal. Pass the leads to push.
export default function PushModal({ open, onClose, leads }) {
  const { toast } = useToast();
  const [campaigns, setCampaigns] = useState([]);
  const [campaignId, setCampaignId] = useState("");
  const [loading, setLoading] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [result, setResult] = useState(null);
  const [summary, setSummary] = useState([]);

  const withEmail = (leads || []).filter((r) => r.email || (r.emails && r.emails[0]));

  const load = async () => {
    setLoading(true);
    setResult(null);
    try {
      const data = await getInstantlyCampaigns();
      setCampaigns(data.items || []);
      if ((data.items || []).length) setCampaignId(data.items[0].id);
    } catch (e) {
      toast({ title: "Couldn't load campaigns", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally {
      setLoading(false);
    }
    getPushSummary().then((d) => setSummary(d.items || [])).catch(() => {});
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const doPush = async () => {
    if (!campaignId || !withEmail.length) return;
    setPushing(true);
    try {
      const res = await pushToInstantly(campaignId, withEmail);
      setResult(res);
      toast({ title: "Pushed to Instantly", description: `${res.uploaded ?? res.sent} leads added.` });
      getPushSummary().then((d) => setSummary(d.items || [])).catch(() => {});
    } catch (e) {
      toast({ title: "Push failed", description: e?.response?.data?.detail || "Please try again.", variant: "destructive" });
    } finally {
      setPushing(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl border border-border w-full max-w-md p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-[18px] font-semibold text-foreground flex items-center gap-2"><Send size={17} className="text-brand-600" /> Push to Instantly</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
        </div>

        {result ? (
          <div className="text-center py-4">
            <CheckCircle2 size={40} className="mx-auto text-brand-600" />
            <p className="mt-3 text-[15px] font-medium text-foreground">Leads pushed successfully</p>
            <div className="mt-3 text-[13px] text-muted-foreground space-y-1">
              <div>Uploaded: <span className="font-medium text-foreground">{result.uploaded ?? result.sent}</span></div>
              {result.duplicates > 0 && <div>Duplicates skipped: {result.duplicates}</div>}
              {result.skipped_no_email > 0 && <div>No email (skipped): {result.skipped_no_email}</div>}
            </div>
            <button onClick={onClose} className="mt-5 px-4 py-2 rounded-lg bg-brand-600 text-white text-[14px] font-medium hover:bg-brand-700">Done</button>
          </div>
        ) : loading ? (
          <div className="py-10 text-center text-muted-foreground"><Loader2 size={22} className="mx-auto animate-spin" /><p className="mt-2 text-[13px]">Loading your campaigns…</p></div>
        ) : campaigns.length === 0 ? (
          <div className="text-center py-4">
            <Info size={28} className="mx-auto text-amber-500" />
            <p className="mt-3 text-[14px] text-foreground font-medium">No campaigns found</p>
            <p className="mt-1 text-[13px] text-muted-foreground">Create a campaign in Instantly first, then come back and push leads into it.</p>
            <a href="https://app.instantly.ai" target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-1 text-[13px] font-medium text-brand-600 hover:underline">Open Instantly <ExternalLink size={13} /></a>
            <div className="mt-3"><button onClick={load} className="text-[12px] text-muted-foreground hover:text-brand-600 inline-flex items-center gap-1"><RotateCcw size={12} /> Reload campaigns</button></div>
          </div>
        ) : (
          <>
            <p className="text-[13px] text-muted-foreground mb-3">Pushing {withEmail.length} leads with an email into:</p>
            <label className="text-[12px] font-medium text-muted-foreground mb-1.5 block">Campaign</label>
            <select value={campaignId} onChange={(e) => setCampaignId(e.target.value)}
              className="w-full border border-border rounded-lg px-3 py-2.5 text-[14px] outline-none focus:border-brand-500 bg-white">
              {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>

            {summary.length > 0 && (
              <div className="mt-4 rounded-lg border border-border bg-secondary/40 p-3">
                <div className="flex items-center gap-1.5 text-[12px] font-medium text-foreground mb-2"><TrendingUp size={13} className="text-brand-600" /> Pushed this week</div>
                <div className="space-y-1">
                  {summary.map((s) => (
                    <div key={s.campaign_id} className="flex items-center justify-between text-[12px]">
                      <span className="text-muted-foreground truncate mr-2">{s.campaign_name}</span>
                      <span className="font-medium text-foreground shrink-0">{s.leads} leads</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <button onClick={onClose} className="px-4 py-2 rounded-lg text-[14px] text-muted-foreground hover:bg-secondary">Cancel</button>
              <button onClick={doPush} disabled={pushing || !campaignId || !withEmail.length} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-brand-600 text-white text-[14px] font-medium disabled:opacity-50 hover:bg-brand-700">
                {pushing ? <><Loader2 size={15} className="animate-spin" /> Pushing…</> : <><Send size={15} /> Push leads</>}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
